// <BookmarkButton/> — a reusable, general-purpose «ذخیره» toggle wired to the real
// bookmarkApi (via pages/bookmark-state.ts). Saved/unsaved state is read from the
// shared saved-keys set (so every button across the SPA reflects a toggle instantly,
// without N calls). Optimistic toggle; on error it reverts and surfaces a toast.
//
// RTL + a11y: aria-pressed reflects saved-state; the accessible name / title flip
// between «ذخیره» (unsaved) and «ذخیره شد» (saved). The star icon turns gold when
// saved (mirrors the existing labs/bookmark visual language).

import React from "react";

import { Icon } from "../icons";
import { useBookmarks } from "../pages/bookmark-state";

export interface BookmarkButtonProps {
  resourceType: "chat_message" | "library" | "lab" | "lesson" | "link";
  resourceId: string;
  /** display snapshot — kept so a deleted resource still renders in the list. */
  title?: string;
  /** where clicking the saved row navigates (in-app route or external https url). */
  targetHref?: string;
  note?: string;
  /** icon size (px). */
  size?: number;
  /** extra className (e.g. "icon-btn" / "mx-ctx-item" host styling). */
  className?: string;
  style?: React.CSSProperties;
  /** "icon" (default, a star button) or "menuitem" (a labelled row for a context menu). */
  variant?: "icon" | "menuitem";
  /** called after a successful toggle with the new saved-state (e.g. to close a menu). */
  onToggled?: (saved: boolean) => void;
}

const SAVED_COLOR = "#f59e0b"; // gold — same accent the labs bookmark + the saved list use

export const BookmarkButton: React.FC<BookmarkButtonProps> = ({
  resourceType,
  resourceId,
  title,
  targetHref,
  note,
  size = 14,
  className,
  style,
  variant = "icon",
  onToggled,
}) => {
  const { isSaved, toggle } = useBookmarks(true);
  const [busy, setBusy] = React.useState(false);
  const saved = isSaved(resourceType, resourceId);

  const onClick = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const next = await toggle({ resourceType, resourceId, title, targetHref, note });
      window.toast?.({
        title: next ? "ذخیره شد" : "حذف شد",
        msg: next ? "به «ذخیره‌ها» اضافه شد." : "از «ذخیره‌ها» حذف شد.",
        kind: "success",
      });
      onToggled?.(next);
    } catch {
      window.toast?.({ title: "خطا", msg: "ذخیره ناموفق بود. دوباره تلاش کنید.", kind: "danger" });
    } finally {
      setBusy(false);
    }
  };

  const label = saved ? "ذخیره شد" : "ذخیره";

  if (variant === "menuitem") {
    return (
      <button
        type="button"
        role="menuitem"
        className={className}
        aria-pressed={saved}
        disabled={busy}
        onClick={(e) => void onClick(e)}
        style={style}
      >
        <span className="mx-ctx-ico" aria-hidden="true" style={{ color: saved ? SAVED_COLOR : "inherit" }}>
          <Icon name="bookmark" size={size} />
        </span>
        <span>{label}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className={className}
      aria-label={label}
      aria-pressed={saved}
      title={label}
      disabled={busy}
      onClick={(e) => void onClick(e)}
      style={{ color: saved ? SAVED_COLOR : "inherit", ...style }}
    >
      <Icon name="star" size={size} />
    </button>
  );
};

export default BookmarkButton;
